import * as React from 'react';
import { v4 } from 'uuid';
import Styled from 'styled-components';

export interface IToggleProps {
  id?: string;
  checked?: boolean;
}

const ToggleLabel = Styled.label`
  display: flex;
  position: relative;
  cursor: pointer;
  align-items: center;
  font-weight: 300;
  font-size: ${({ theme: { button } }) => button.fontSize};
  line-height: 1.2;
  transition: color 100ms ease-out;
  padding: 0.125em 3.5em 0.125em 0;
  border: 1px solid transparent;

  &::before {
    content: '';
    position: absolute;
    display: flex;
    align-items: center;
    right: 0;
    padding: 0.563em 1.125em;
    border-radius: 40px;
    border-width: ${({ theme: { button } }) => button.borderWidth};
    border-style: solid;
    border-color: ${({ theme }) => theme.colorSlate70};
    transition: border 100ms ease-out;
  }

  &::after {
    content: '';
    position: absolute;
    display: flex;
    align-items: center;
    border-radius: 50%;
    width: 0.5em;
    height: 0.5em;
    right: 1.5em;
    background: ${({ theme }) => theme.colorSlate20};
    transition: background 100ms ease-out, right 100ms ease-out;
  }
`;

const ToggleWrap = Styled.div`
  display: flex;
  align-items: center;
  position: relative;

  label {
    color: ${({ theme }) => theme.colorSlate70};
  }

  /* stylelint-disable no-descending-specificity */
  & > input:focus ~ label {
    outline: ${({ theme: { outline } }) => outline};
    border: none;
  }

  &:focus,
  &:active,
  &:hover {
    label {
      color: ${({ theme: { text } }) => text.colorText};

      &::before {
        border-color: ${({ theme }) => theme.text.colorText};
      }
    }
  }
`;

const ToggleInput = Styled.input`
  width: 1px;
  height: 1px;
  position: absolute;
  top: 0;
  left: -999px;
  clip: rect(1px, 1px, 1px, 1px);
  overflow: hidden;
  white-space: nowrap;

  &:checked ~ label {
    color: ${({ theme: { text } }) => text.colorText};

    &::after {
      right: 0.5em;
      background: ${({ theme: { checkbox } }) => checkbox.colorBackgroundChecked};
    }

    &::before {
      border-color: ${({ theme }) => theme.text.colorText};
    }
  }

  &:focus ~ label {
    border: 1px solid ${({ theme: { input } }) => input.colorFocus};
  }
`;
 /* stylelint-enable no-descending-specificity */

export class Toggle extends React.PureComponent<React.InputHTMLAttributes<HTMLInputElement> & IToggleProps> {
  private readonly _id: string = v4();

  public render () {
    const { children, checked, defaultChecked, id, onClick, tabIndex, ...restProps } = this.props;

    return (
      <ToggleWrap {...restProps}>
        <ToggleInput
          id={this._id}
          onClick={onClick}
          onKeyPress={this.handleEnterKeyPress}
          checked={checked}
          defaultChecked={defaultChecked}
          tabIndex={tabIndex}
          data-id={id}
          aria-checked={checked}
          type='Checkbox'
        />
        <ToggleLabel
          htmlFor={this._id}
          data-id={id}
        >
          {children}
        </ToggleLabel>
      </ToggleWrap>
    );
  }

  private readonly handleEnterKeyPress: React.KeyboardEventHandler<HTMLInputElement> = e => {
    const ENTER_KEYCODE: number = 13;
    if (e.type === 'keypress' && e.key === 'Enter' || e.which === ENTER_KEYCODE) {
      e.currentTarget.click();
    }
  }
}

export default Toggle;
